import { BaseCurrency, Currency, isDefined, Loadable, NbSelectItemOption } from '@eih-netbroker/common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { ErrorWidgetContent } from '../../../../shared/components/error-widget-content/ErrorWidgetContent';
import { FeedbackBox } from '../../../../shared/components/feedback-box/FeedbackBox';
import { NbLoader } from '../../../../shared/components/loader/Loader';
import { NbSelect } from '../../../../shared/components/select/Select';
import { useDeposit } from '../../services/useDeposit';
import { CashDepositExternalDetailsRow } from './CashDepositExternalDetailsRow';
import classes from './CashDepositExternalTabContent.module.scss';

export const CashDepositExternalTabContent = () => {
  const { translation } = useLocale();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyTimeoutRef = useRef<number | null>(null);
  const { depositDetails, selectedExternalCurrency, setSelectedExternalCurrency, refreshDepositDetails } = useDeposit();

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const currencyOptions = useMemo<Array<NbSelectItemOption<string>>>(() => {
    if (!depositDetails.isLoaded) {
      return [];
    }

    return depositDetails.bankTransferInfoItems.map(d => ({
      id: d.currencyCode,
      name:
        translation.portfolio.currencies[d.currencyCode as keyof typeof translation.portfolio.currencies] ??
        d.currencyCode,
    }));
  }, [depositDetails, translation]);

  const selectedExternalDetails = useMemo(
    () =>
      isDefined(selectedExternalCurrency) && depositDetails.isLoaded
        ? depositDetails.bankTransferInfoItems.find(d => d.currencyCode === selectedExternalCurrency)
        : undefined,
    [depositDetails, selectedExternalCurrency],
  );

  const handleCopy = async (key: string, value: string) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedKey(null);
      }, 1500);
    } catch (error) {
      setCopiedKey(null);
    }
  };

  if (depositDetails.isLoading || Loadable.isUninitialized(depositDetails)) {
    return (
      <div className={classes.emptyContainer}>
        <NbLoader
          horizontalCenter
          verticalCenter
        />
      </div>
    );
  }

  if (depositDetails.hasError || !isDefined(selectedExternalDetails)) {
    return (
      <div className={classes.error500Container}>
        <ErrorWidgetContent onClick={refreshDepositDetails} />
      </div>
    );
  }

  return (
    <div className={classes.content}>
      <div className={classes.selectorBlock}>
        <p className={classes.selectorLabel}>{translation.cashAction.deposit.external.currencyLabel}</p>
        <NbSelect<string>
          value={selectedExternalCurrency}
          options={currencyOptions}
          onChange={event => setSelectedExternalCurrency(event.target.value as Currency)}
          size="small"
          className={classes.currencySelect}
        />
      </div>

      <div className={classes.details}>
        <div className={classes.rows}>
          <p className={classes.detailsTitle}>{translation.cashAction.deposit.external.detailsTitle}</p>
          <CashDepositExternalDetailsRow
            label={translation.cashAction.deposit.external.detailsLabel.clientName}
            value={selectedExternalDetails.clientName}
            copiedKey={copiedKey}
            handleCopy={handleCopy}
          />
          <CashDepositExternalDetailsRow
            label={translation.cashAction.deposit.external.detailsLabel.accountNumber}
            value={selectedExternalDetails.accountNumber}
            copiedKey={copiedKey}
            handleCopy={handleCopy}
          />
          {selectedExternalDetails.iban && (
            <CashDepositExternalDetailsRow
              label={translation.cashAction.deposit.external.detailsLabel.iban}
              value={selectedExternalDetails.iban}
              copiedKey={copiedKey}
              handleCopy={handleCopy}
            />
          )}
          <CashDepositExternalDetailsRow
            label={translation.cashAction.deposit.external.detailsLabel.bicCode}
            value={selectedExternalDetails.bicCode}
            copiedKey={copiedKey}
            handleCopy={handleCopy}
          />
          {selectedExternalDetails.announcement && (
            <CashDepositExternalDetailsRow
              label={translation.cashAction.deposit.external.detailsLabel.announcement}
              value={selectedExternalDetails.announcement}
              copiedKey={copiedKey}
              handleCopy={handleCopy}
            />
          )}
        </div>

        <div className={classes.feedbackContainer}>
          {selectedExternalCurrency !== BaseCurrency && (
            <FeedbackBox
              type="warning"
              title={translation.cashAction.deposit.external.warning.title}
              message={translation.cashAction.deposit.external.warning.message}
              isWithoutBorder
            />
          )}

          <FeedbackBox
            type="info"
            title={translation.cashAction.deposit.external.info.title}
            message={translation.cashAction.deposit.external.info.message}
            isWithoutBorder
          />
        </div>
      </div>
    </div>
  );
};
